import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { accounting, type ReconBreak } from "@/api/accounting";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, FormError, Input, Label } from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Treasury reconciliation — daily bank-vs-ledger runs, unresolved breaks, and
 * imported bank statements. Read-only; produced by the reconciliation daemon.
 */
export function AdminReconciliationPage() {
  const runs = useQuery({ queryKey: ["recon-runs"], queryFn: () => accounting.reconRuns() });
  const breaks = useQuery({ queryKey: ["recon-breaks", false], queryFn: () => accounting.reconBreaks(false) });
  const statements = useQuery({ queryKey: ["bank-statements"], queryFn: () => accounting.bankStatements() });
  const [resolving, setResolving] = useState<ReconBreak | null>(null);

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">Reconciliation</h1>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Unresolved breaks</span>
            <Badge tone={(breaks.data?.length ?? 0) > 0 ? "bad" : "good"}>{breaks.data?.length ?? 0}</Badge>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {breaks.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !breaks.data || breaks.data.length === 0 ? (
            <EmptyState title="All reconciled" description="No unresolved bank-vs-ledger breaks." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Note</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {breaks.data.map((b) => (
                  <tr key={b.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{b.kind}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {b.amount} {b.currency}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{b.note ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button variant="ghost" onClick={() => setResolving(b)}>
                        Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Recent runs</span>
        </CardHeader>
        <CardBody className="p-0">
          {!runs.data || runs.data.length === 0 ? (
            <EmptyState title="No runs" description="Daily reconciliation runs appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Date</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Bank</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Ledger</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Diff</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((r) => (
                  <tr key={r.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {r.run_date ? new Date(r.run_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.bank_total}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.ledger_total}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.difference}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={r.status === "balanced" || r.status === "ok" ? "good" : "warn"}>
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Bank statements</span>
        </CardHeader>
        <CardBody className="p-0">
          {!statements.data || statements.data.length === 0 ? (
            <EmptyState title="No statements" description="Imported bank statements appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Date</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Opening</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Closing</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Lines</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Source</th>
                </tr>
              </thead>
              <tbody>
                {statements.data.map((s) => (
                  <tr key={s.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {s.statement_date ? new Date(s.statement_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{s.opening_balance}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{s.closing_balance}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{s.line_count}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{s.source ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {resolving && <ResolveBreakModal brk={resolving} onClose={() => setResolving(null)} />}
    </div>
  );
}

function ResolveBreakModal({ brk, onClose }: { brk: ReconBreak; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => accounting.resolveBreak(brk.id, note || undefined),
    onSuccess: () => {
      toast.push("Break resolved", "success");
      qc.invalidateQueries({ queryKey: ["recon-breaks", false] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="Resolve reconciliation break">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1.5 text-xs text-term-dim">
          <span className="text-term-mute">{brk.kind}</span>{" "}
          <span className="font-mono text-term-fg">{brk.amount} {brk.currency}</span>
        </div>
        <div>
          <Label>Resolution note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="how it was reconciled" />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submit.isPending}>
            Mark resolved
          </Button>
        </div>
      </form>
    </Modal>
  );
}
